import React, { useRef, type FunctionComponent } from 'react';

import { Alert } from '@/core/tamagoshi/icons/Alert';
import { Text } from '@/core/tamagoshi/Text/Text';
import { Typography } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { useTrackViewedElement } from '@/domains/core/tracking/hooks/useTrackViewedElement';
import { BillieBanner } from '@/domains/customerFinanceOperations/components/BillieBanner/BillieBanner';
import { DynamicMDRankingPlaylist } from '@/domains/productDiscovery/ArticleReviews/modules/mdRankingPlaylist/DynamicMDRankingPlaylist';
import { ReviewsBlock } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/ReviewsBlock';
import { MassLandings } from '@/domains/productDiscovery/ProductPage/b2c/components/MassLandings/MassLandings';
import { AdviceBlock } from '@/domains/productDiscovery/ProductPage/b2c/modules/ProductContainer/components/AdviceBlock/AdviceBlock';
import { Seller } from '@/domains/productDiscovery/ProductPage/b2c/modules/Seller/Seller';
import { BrandBlock } from '@/domains/productDiscovery/ProductPage/common/components/BrandBlock/BrandBlock';
import { BrandLink } from '@/domains/productDiscovery/ProductPage/common/components/BrandLink/BrandInfo';
import { Breadcrumbs } from '@/domains/productDiscovery/ProductPage/common/components/Breadcrumbs/Breadcrumbs';
import { DescriptionPreview } from '@/domains/productDiscovery/ProductPage/common/components/DescriptionPreview/DescriptionPreview';
import { ProductLabels } from '@/domains/productDiscovery/ProductPage/common/components/ProductLabels/ProductLabels';
import { Section } from '@/domains/productDiscovery/ProductPage/common/components/Section/Section';
import { SectionBlock } from '@/domains/productDiscovery/ProductPage/common/components/SectionBlock/SectionBlock';
import { SidePanelContainer } from '@/domains/productDiscovery/ProductPage/common/components/SidePanelContainer/SidePanelContainer';
import { WishlistButton } from '@/domains/productDiscovery/ProductPage/common/components/WishlistButton/WishlistButton';
import { DeliveryDisclaimer } from '@/domains/productDiscovery/ProductPage/common/modules/Delivery/Disclaimer/DeliveryDisclaimer';
import { ProductComparatorContainer } from '@/domains/productDiscovery/ProductPage/common/modules/ProductComparator/ProductComparator.container';
import { SurfaceInput } from '@/domains/productDiscovery/ProductPage/common/modules/QuantitySelector/SurfaceInput';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import { PlaylistProductHistory } from '@/domains/productDiscovery/Recommendation/components/PlaylistGraphQL/components/PlaylistProductHistory/PlaylistProductHistory';
import { ProductBundleContainer } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/ProductBundle.container';
import { useRecommendationFromConfig } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/useRecommendationFromConfig';
import { HighlightedProductContainer } from '@/domains/sellerAdsManagement/SponsoredProducts/components/HighlightedProduct/HighlightedProduct.container';
import { CarbonScoreDetailContainer } from '@/domains/sustainability/carbonScore/components/CarbonScoreDetail/CarbonScoreDetailContainer';
import { CashbackOfferTag } from '@/productDiscovery/CommercialAnimation/components/CashbackOfferTag/CashbackOfferTag';
import { useB2bPriceAdvantage } from '@/productDiscovery/ProductPage/b2b/hooks/useB2bPriceAdvantage';
import { Klarna } from '@/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PaymentInformations/Klarna';
import { PaymentInformations } from '@/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PaymentInformations/PaymentInformations';
import { MFAlternativeContainer } from '@/productDiscovery/ProductPage/b2c/modules/MFAlternative/MFAlternative.container';
import FrequentlyBoughtTogetherPlaylist from '@/productDiscovery/ProductPage/b2c/modules/ProductContainer/components/Playlists/FrequentlyBoughtTogetherPlaylist/FrequentlyBoughtTogetherPlaylist';
import SimilarityPlaylist from '@/productDiscovery/ProductPage/b2c/modules/ProductContainer/components/Playlists/SimilarityTopPlaylist/SimilarityTopPlaylist';
import { breadcrumbsMobileTitle } from '@/productDiscovery/ProductPage/b2c/modules/ProductContainer/translations';
import { Title } from '@/productDiscovery/ProductPage/b2c/modules/Title/Title';
import { ModelsListManager } from '@/productDiscovery/ProductPage/b2c/modules/Variants/ModelsListManager';
import { LazyCompactVariantSelector } from '@/productDiscovery/ProductPage/b2c/modules/Variants/VariantSelector/LazyCompactVariantSelector';
import { AddToCart } from '@/productDiscovery/ProductPage/common/components/AddToCart/AddToCart';
import { AddToCartModal } from '@/productDiscovery/ProductPage/common/components/AddToCart/AddToCartModal';
import { stockNotAvailable } from '@/productDiscovery/ProductPage/common/components/AddToCart/translations';
import { BulkPurchaseTable } from '@/productDiscovery/ProductPage/common/components/BulkPurchaseTable/BulkPurchaseTable';
import { Carousel } from '@/productDiscovery/ProductPage/common/components/Carousel/Carousel';
import { GalleryWall } from '@/productDiscovery/ProductPage/common/components/GalleryWall/GalleryWall';
import {
  MaxStockReached,
  ProductQuantity,
  VatPrice,
} from '@/productDiscovery/ProductPage/common/modules/Price/PriceAndQuantity';
import { SponsoredPlaylistContainer } from '@/productDiscovery/ProductPage/common/modules/SponsoredPlaylist/SponsoredPlaylist.container';
import { useXBorderVat } from '@/productDiscovery/ProductPage/hooks/useXBorderVat';
import { useFetchSponsoredData } from '@/productDiscovery/ProductPage/modules/SponsoredData/useFetchSponsoredData';
import { TopBannerProductContainer } from '@/sellerAdsManagement/SponsoredProducts/components/TopBannerProduct/TopBannerProduct.container';

import { Block } from './common/components/Block/Block';
import { HeaderRating } from './common/components/HeaderRating/HeaderRating';
import { MainCoupon } from './common/components/MainCoupon/MainCoupon';
import { SectionButtons } from './common/components/SectionButtons/SectionButtons';
import { StickyElements } from './common/components/StickyElements/StickyElements';

export const ProductContainerRevamp: FunctionComponent = () => {
  const add2CartElementRef = useRef<HTMLDivElement | null>(null);

  const { productId, isSellable, modelId, articleId } = useDetail();
  const noRecoBrands = [1145];
  const { id } = useBrand();
  const shouldShowExtraPlaylists = !noRecoBrands.includes(id ?? 0); // Dyson brand

  const b2bPriceAdvantage = useB2bPriceAdvantage(modelId) ?? 0;
  const vatStatus = useXBorderVat();

  /**
   * Used to start recommendation and sponsored recommendation fetching. Used
   by multiple recommendation components in this body ("body" class name).
   */
  const [bodyRef, isInView] = useTrackViewedElement({
    delay: 100,
    threshold: 0,
    rootMargin: '250px', // 250px before the element is in view
    resetViewedWithSearchParams: true,
    triggerOnce: true,
  });

  return (
    <>
      <IsolatedRecommendationInit
        startFetching={isInView}
        key={`sponsored-data-${productId}`}
      />
      <AddToCartModal />
      <SidePanelContainer />
      <TopBannerProductContainer />
      <StickyElements ref={add2CartElementRef} />

      {/* Screen Container section*/}
      <div
        className="flex flex-col items-center gap-l scroll-smooth
          bg-foundation-primary desktop:px-xl"
      >
        <SectionBlock className="flex flex-col gap-m px-0">
          <Breadcrumbs className="!hidden first:mt-m desktop:!flex" />

          {/* Details Container section */}
          <div className="flex flex-col desktop:flex-row desktop:gap-m">
            {/* Gallery section */}
            <div
              className="h-min flex-1 items-stretch gap-m desktop:sticky
                desktop:top-s"
            >
              <Carousel className="desktop:hidden" />
              <div className="hidden desktop:block">
                <GalleryWall />
              </div>
            </div>

            {/* Main Product Details section */}
            <div
              className="flex flex-1 flex-col gap-l rounded-container
                bg-foundation-inverted py-m"
            >
              <Section>
                <div className="flex flex-col gap-m">
                  <div className="flex flex-col gap-s">
                    <Title />
                    <BrandLink />
                    <ProductLabels />
                  </div>
                  <HeaderRating />
                </div>
              </Section>

              {productId && (
                <Section>
                  <CashbackOfferTag productId={productId.toString()} />
                </Section>
              )}

              <Section>
                <div data-abtasty-id="product-page-add-to-cart-block" />
                <div className="flex flex-col items-start gap-m">
                  <VatPrice vatStatus={vatStatus} />
                  <Klarna />
                  <BillieBanner />
                  <PaymentInformations />
                </div>
              </Section>

              <div className="desktop:px-m">
                <ModelsListManager />
              </div>

              <Section>
                <LazyCompactVariantSelector />
              </Section>

              <Section>
                <BulkPurchaseTable vatStatus={vatStatus} />
              </Section>

              <Section>
                <MainCoupon />
              </Section>

              {!isSellable && (
                <Section>
                  <Text>
                    <Alert /> {stockNotAvailable}
                  </Text>
                </Section>
              )}

              <Section>
                <div className="flex flex-col gap-m">
                  <SurfaceInput />

                  <div className="flex h-[48px] gap-s" ref={add2CartElementRef}>
                    <ProductQuantity />
                    <AddToCart
                      b2bPriceAdvantage={b2bPriceAdvantage}
                      vatStatus={vatStatus}
                    />
                    <WishlistButton />
                  </div>

                  <MaxStockReached />
                </div>
              </Section>

              <Section withBorder>
                <HighlightedProductContainer />
              </Section>

              <SectionButtons />

              <Section withBorder>
                <MFAlternativeContainer />
              </Section>

              <Section>
                <Seller isRevamp />
              </Section>
            </div>
          </div>
          <div ref={bodyRef} className="absolute bottom-0" />
        </SectionBlock>

        {isSellable && (
          <Block className="desktop:px-xl">
            <ProductBundleContainer />
          </Block>
        )}

        <SectionBlock>
          <SponsoredPlaylistContainer />
        </SectionBlock>

        <SectionBlock>
          <SimilarityPlaylist />
        </SectionBlock>

        <Block>
          <ProductComparatorContainer key={articleId} />
        </Block>

        <SectionBlock>
          <FrequentlyBoughtTogetherPlaylist />
        </SectionBlock>

        <Block className="wide:px-[232px]">
          <DescriptionPreview />
        </Block>

        <Block className="wide:px-[232px]">
          <ReviewsBlock />
        </Block>

        <Block className="wide:px-[232px]">
          <CarbonScoreDetailContainer anchorId="carbon-score-detail" />
        </Block>

        {shouldShowExtraPlaylists && (
          <SectionBlock>
            <DynamicMDRankingPlaylist />
          </SectionBlock>
        )}

        <SectionBlock className="px-0">
          <AdviceBlock />
        </SectionBlock>

        <Block className="wide:px-[232px]">
          <BrandBlock />
        </Block>

        {shouldShowExtraPlaylists && (
          <SectionBlock>
            <PlaylistProductHistory productId={productId} />
          </SectionBlock>
        )}

        <SectionBlock className="flex flex-col gap-s px-m desktop:hidden">
          <Typography scale="small" weight="bold">
            {breadcrumbsMobileTitle}
          </Typography>
          <Breadcrumbs />
        </SectionBlock>

        <SectionBlock>
          <MassLandings />
        </SectionBlock>

        <SectionBlock>
          <DeliveryDisclaimer />
        </SectionBlock>
        <span />
      </div>
    </>
  );
};

interface IsolatedRecommendationInit {
  startFetching: boolean;
}

/**
 * Used to start recommendation fetching. Isolated
 * from the rest of the component to avoid unnecessary re-renders.
 * @param {boolean} startFetching - Whether to start fetching recommendations.
 * @returns {null} - Nothing to render.
 */
const IsolatedRecommendationInit = ({
  startFetching,
}: IsolatedRecommendationInit): null => {
  useFetchSponsoredData();
  useRecommendationFromConfig(startFetching);
  return null;
};

IsolatedRecommendationInit.displayName = 'IsolatedRecommendationInit';
